import { useAsyncData } from 'nuxt/app';
import {
  onScopeDispose,
  ref,
  toValue,
  watch,
  type MaybeRefOrGetter,
} from 'vue';
import { useGetData } from '~/composables/useGetData';
import type { Party } from '~/types/Candidates';
import type { ApiMedia, LaravelPaginatedResponse } from '~/types/plugins/api';
import { SEARCH_DEBOUNCE_MS } from '~/utils/search';

interface ApiParty {
  id: number;
  name: string;
  abbreviation: string | null;
  slug: string;
  type: 'major' | 'minor';
  description: string | null;
  website: string | null;
  logo: ApiMedia | null;
  candidates_count?: number;
  campaigns_count?: number;
  lobbyists_count?: number;
}

function mapParty(raw: ApiParty): Party {
  return {
    id: raw.slug,
    name: raw.name,
    abbreviation: raw.abbreviation ?? '',
    logo: raw.logo?.thumb_url ?? raw.logo?.url ?? null,
    classification: raw.type,
    description: raw.description ?? '',
    candidatesCount: raw.candidates_count ?? 0,
    campaignsCount: raw.campaigns_count ?? 0,
    lobbyistsCount: raw.lobbyists_count ?? 0,
    website: raw.website ?? '',
    // Not requested on the list endpoint — only the detail response has
    // per-party sources.
    sources: [],
  };
}

export interface UsePartiesOptions {
  /**
   * Gates fetching entirely. Defaults to true. search.vue passes its
   * `hasQuery` computed here so visiting `/search` with an empty query issues
   * no request — while `enabled` is false, neither the initial fetch nor a
   * suburb/query change triggers one. Flipping it back to true does not
   * itself trigger a fetch — `suburbId`/`query` need to actually change too,
   * which is exactly what happens once a query is typed.
   */
  enabled?: MaybeRefOrGetter<boolean>;
}

export function useParties(
  suburbId: MaybeRefOrGetter<string | null> = null,
  query: MaybeRefOrGetter<string> = '',
  options: UsePartiesOptions = {},
) {
  const { enabled = true } = options;
  const parties = ref<Party[]>([]);
  const pending = ref(true);
  const error = ref<unknown>(null);

  // Matches useCandidates/useCampaigns: the list is server-filtered, so binding
  // the raw query straight into `load` would fire a request per keystroke.
  // `filter[search]` matches name and abbreviation, which is what the pages
  // used to do client-side over a single 50-row page — that silently missed
  // any party past the first page.
  const debouncedQuery = ref(toValue(query));
  let queryTimer: ReturnType<typeof setTimeout> | undefined;
  watch(
    () => toValue(query),
    (value) => {
      clearTimeout(queryTimer);
      queryTimer = setTimeout(() => {
        debouncedQuery.value = value;
      }, SEARCH_DEBOUNCE_MS);
    },
  );
  onScopeDispose(() => clearTimeout(queryTimer));

  // Guards against a slower in-flight request for a previous (or cleared)
  // suburb resolving after a newer one has already landed — same
  // stale-overwrite race useVotingAreaCounts/useExploreCounts guard against.
  let requestId = 0;

  const load = async () => {
    const id = toValue(suburbId);
    const search = debouncedQuery.value.trim();
    const currentRequestId = ++requestId;

    pending.value = true;
    error.value = null;

    try {
      const { execute } =
        await useGetData<LaravelPaginatedResponse<ApiParty>>();
      const result = await execute('parties', {
        params: {
          include: 'candidatesCount,campaignsCount,lobbyistsCount',
          per_page: 50,
          ...(id ? { 'filter[suburb_id]': id } : {}),
          ...(search ? { 'filter[search]': search } : {}),
        },
      });
      if (currentRequestId !== requestId) return;
      parties.value = (result?.data ?? []).map(mapParty);
    } catch (err) {
      if (currentRequestId !== requestId) return;
      error.value = err;
    } finally {
      if (currentRequestId === requestId) {
        pending.value = false;
      }
    }
  };

  // The key encodes every input of the fetch — see useCandidates for why a
  // key that ignores the filters replays stale (or a disabled mount's null)
  // data after the fullPath-keyed page remount that follows a settled query
  // change, instead of fetching.
  const listKey = [
    'parties',
    toValue(enabled),
    toValue(suburbId) ?? '',
    toValue(query).trim(),
  ].join(':');

  // The FIRST load is wrapped in `useAsyncData` so Nuxt awaits it on the
  // server — matching useCandidates/useCampaigns/useExploreCounts in this
  // same family. Previously this was driven by an `{ immediate: true }`
  // watch, a fire-and-forget call Nuxt never awaited: /parties rendered
  // skeletons on the server and refetched the 50-row list after hydration.
  const { data: initialResult, error: asyncError } = useAsyncData<
    Party[] | null
  >(
    listKey,
    async () => {
      if (!toValue(enabled)) return null;
      await load();
      // `load()` swallows its own exception into the local `error` ref, which
      // does not survive hydration. Rethrow so the failure crosses the
      // boundary via useAsyncData's error (Nuxt wraps it in a serializable
      // NuxtError) instead of hydrating into a false "no parties" state.
      if (error.value) throw error.value;
      return parties.value;
    },
    { default: () => null },
  );

  watch(
    initialResult,
    (result) => {
      if (!result) return;
      parties.value = result;
      pending.value = false;
    },
    { immediate: true },
  );

  watch(
    asyncError,
    (value) => {
      if (!value) return;
      error.value = value;
      pending.value = false;
    },
    { immediate: true },
  );

  // While disabled nothing fetches, and the fetcher's `null` never reaches
  // either seed watcher — this is what clears the initial pending state now
  // that the immediate watch below is gone (search.vue's empty-query visit
  // must not sit on a skeleton forever).
  if (!toValue(enabled)) pending.value = false;

  // Guarded by `enabled` so a caller like search.vue (empty query on first
  // visit) issues no request at all, rather than fetching the unscoped
  // 50-row list just to have `hasQuery` hide it from the template. Once a
  // real query is typed, `debouncedQuery` — one of this watch's own sources —
  // changes too, so the fetch fires naturally without any extra wiring.
  watch([() => toValue(suburbId), debouncedQuery], () => {
    if (!toValue(enabled)) {
      pending.value = false;
      return;
    }
    load();
  });

  return { parties, pending, error };
}
